import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { configuracionAPI, categoriasAPI, marcasAPI, proveedoresAPI } from '../services/api';
import { useAuth } from './AuthContext';

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [config, setConfig] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await configuracionAPI.getAll();
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  }, []);

  const loadCatalogos = useCallback(async () => {
    try {
      const [catRes, marcasRes, provRes] = await Promise.all([
        categoriasAPI.getAll(),
        marcasAPI.getAll(),
        proveedoresAPI.getAll()
      ]);
      if (catRes.data.success) setCategorias(catRes.data.data);
      if (marcasRes.data.success) setMarcas(marcasRes.data.data);
      if (provRes.data.success) setProveedores(provRes.data.data);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  }, []);

  useEffect(() => {
    // Solo cargar datos cuando el usuario está autenticado
    if (isAuthenticated) {
      const init = async () => {
        setLoading(true);
        await Promise.all([loadConfig(), loadCatalogos()]);
        setLoading(false);
      };
      init();
    } else {
      // Si no está autenticado, no cargamos datos pero sí marcamos como no-loading
      setLoading(false);
    }
  }, [isAuthenticated, loadConfig, loadCatalogos]);

  const formatCurrency = (value) => {
    const simbolo = config.simbolo_moneda || '$';
    const num = parseFloat(value || 0);
    // Formato Ecuador: punto para miles, coma para decimales → $20.152,50
    const formatted = num.toLocaleString('es-EC', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${simbolo}${formatted}`;
  };

  const formatNumber = (value) => {
    const num = parseInt(value || 0);
    return num.toLocaleString('es-EC');
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const value = {
    config, setConfig,
    categorias, marcas, proveedores,
    loading, sidebarOpen, toggleSidebar,
    formatCurrency, formatNumber,
    refreshCatalogos: loadCatalogos
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
